import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { makeStyles } from "@material-ui/core/styles"

import FormControl from "@material-ui/core/FormControl"
import TextField from "@material-ui/core/TextField"
import InputAdornment from "@material-ui/core/InputAdornment"
import InputLabel from "@material-ui/core/InputLabel"
import Select from "@material-ui/core/Select"
import Button from "@material-ui/core/Button"
import Checkbox from "@material-ui/core/Checkbox"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import Box from "@material-ui/core/Box"
import FormHelperText from "@material-ui/core/FormHelperText"
import Typography from "@material-ui/core/Typography"

import AccountCircleIcon from "@material-ui/icons/AccountCircle"
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail"

import AvatarInput from "components/partials/AvatarInput"
import PasswordResetDialog from "./PasswordResetDialog"
import { HelpTooltip, UserRoleChip } from "components/widgets"
import { AuthApi, CompaniesApi } from "common/api"
import { roles } from "../utils/common"
import { filterRoles } from "../utils/filterRoles"
import { PasswordTextField, MenuItem } from "components/partials"
import { useAppSelector } from "common/hooks"

const useStyles = makeStyles(theme => ({
    formControl: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(4),
        alignItems: "center",

        // Adjustment to align checkbox with text input
        "& .MuiFormControlLabel-root": {
            position: "relative",
            top: 8
        }
    },
    checkbox: {
        marginRight: 0,
        "& .MuiFormControlLabel-label": {
            flexGrow: 1
        }
    }
}))

const Form = props => {
    const { formData, error, onFormChange, setFormData, formProps } = props
    const { t } = useTranslation()
    const isCreatingUser = formData.id === undefined

    const classes = useStyles()
    const [isResettingPassword, setIsResettingPassword] = useState(false)
    const [companies, setCompanies] = useState(null)
    const [showConfirmResetPassword, setShowConfirmResetPassword] = useState(
        false
    )

    const { user: currentUser } = useAppSelector(state => state.auth)
    const { current: initialCompany } = useRef(formData.company)

    const isSuperadmin = currentUser?.role === "Superadmin"
    const isEditMode = Boolean(formData.id)
    const hasCompanies = Array.isArray(companies)
    const isEnabled = Boolean(formData.enabled)
    const isReadOnly = !isCreatingUser && !isSuperadmin && !isEnabled

    const rolesDropdown = isEditMode || isSuperadmin ? roles : filterRoles

    useEffect(() => {
        if (!isSuperadmin) return

        CompaniesApi.get().then(response => {
            setCompanies(response.data)
        })
    }, [isSuperadmin])

    const onChange = key => event => {
        let { value, checked } = event.target

        if (event.target.type === "checkbox") {
            onFormChange(key, checked)
        } else {
            if (hasCompanies && key === "company") {
                value = companies.find(({ id }) => id === value)
            }

            onFormChange(key, value)
        }
    }

    function onChangeUserEnabled(event) {
        setFormData(prevState => ({
            ...prevState,
            enabled: event.target.checked,
            company: initialCompany
        }))
    }

    function onClickSendPasswordReset(event) {
        event.preventDefault()
        setShowConfirmResetPassword(true)
    }

    async function onConfirmSendPasswordReset() {
        if (typeof formData.email !== "string") return
        setIsResettingPassword(true)

        try {
            await AuthApi.requestPasswordReset(formData.email)
        } finally {
            setIsResettingPassword(false)
        }
    }

    function onCloseConfirmResetPassword() {
        setShowConfirmResetPassword(false)
    }

    function onChangeProfile(base64Image) {
        onFormChange("profileImage", base64Image)
    }

    return (
        <form {...formProps}>
            <FormControl fullWidth style={{ display: "flex", alignItems: "center" }}>
                <AvatarInput
                    onChange={onChangeProfile}
                    AvatarProps={{
                        src: formData?.profileImage || undefined
                    }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal" className={classes.formControl}>
                <TextField
                    required
                    label={t("view.admin.firstName")}
                    value={formData.firstName || ""}
                    onChange={onChange("firstName")}
                    error={Boolean(error?.firstName)}
                    helperText={error?.firstName}
                    InputProps={{
                        readOnly: isReadOnly,
                        disabled: isReadOnly,
                        endAdornment: (
                            <InputAdornment position="end">
                                <AccountCircleIcon />
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    required
                    label={t("view.admin.lastName")}
                    value={formData.lastName || ""}
                    onChange={onChange("lastName")}
                    error={Boolean(error?.lastName)}
                    helperText={error?.lastName}
                    InputProps={{
                        readOnly: isReadOnly,
                        disabled: isReadOnly,
                        endAdornment: (
                            <InputAdornment position="end">
                                <AccountCircleIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal" className={classes.formControl}>
                <TextField
                    required
                    label={t("view.admin.email")}
                    type="email"
                    autoComplete="username"
                    value={formData.email || ""}
                    onChange={onChange("email")}
                    error={Boolean(error?.email)}
                    helperText={error?.email}
                    InputProps={{
                        readOnly: isReadOnly,
                        disabled: isReadOnly,
                        endAdornment: (
                            <InputAdornment position="end">
                                <AlternateEmailIcon />
                            </InputAdornment>
                        )
                    }}
                />

                <FormControlLabel
                    className={classes.checkbox}
                    control={
                        <Checkbox
                            name="verified"
                            color="primary"
                            checked={formData.verified || false}
                            onChange={onChange("verified")}
                            disabled={isReadOnly}
                            readOnly={isReadOnly}
                        />
                    }
                    label={
                        <Box display="flex">
                            <Typography>{t("view.admin.verifiedCheckboxLabel")}</Typography>
                            <HelpTooltip
                                title={t("view.adminUsers.verifiedCheckboxTooltip")}
                                IconProps={{ style: { marginLeft: "auto" } }}
                            />
                        </Box>
                    }
                />
            </FormControl>

            <FormControl fullWidth margin="normal" className={classes.formControl}>
                <FormControl>
                    <InputLabel>{t("view.admin.role")} *</InputLabel>
                    <Select
                        required
                        value={formData.role || ""}
                        onChange={onChange("role")}
                        error={Boolean(error?.role)}
                        disabled={isReadOnly}
                        readOnly={isReadOnly}
                    >
                        {rolesDropdown.map((role, index) => (
                            <MenuItem key={index} value={role}>
                                <UserRoleChip role={role} />
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText error>{error?.role}</FormHelperText>
                </FormControl>

                <FormControlLabel
                    className={classes.checkbox}
                    control={
                        <Checkbox
                            name="enabled"
                            color="primary"
                            checked={isEnabled}
                            onChange={onChangeUserEnabled}
                            disabled={isReadOnly || !isSuperadmin}
                            readOnly={isReadOnly || !isSuperadmin}
                        />
                    }
                    label={
                        <Box display="flex">
                            <Typography>{t("view.adminUsers.userEnabledLabel")}</Typography>
                            <HelpTooltip
                                title={t("view.adminUsers.userEnabledTooltip")}
                                IconProps={{ style: { marginLeft: "auto" } }}
                            />
                        </Box>
                    }
                />
            </FormControl>

            <FormControl fullWidth margin="normal" className={classes.formControl}>
                <Box display="flex" alignItems="center">
                    <PasswordTextField
                        required={formData.verified && isCreatingUser}
                        label={t("view.admin.password")}
                        value={formData.password || ""}
                        onChange={onChange("password")}
                        error={Boolean(error?.password)}
                        helperText={error?.password}
                        CheckListProps={{
                            show: false
                        }}
                        disabled={isReadOnly || (isCreatingUser && !formData.verified)}
                        autoComplete="new-password"
                    />
                    <HelpTooltip
                        title={t("view.adminUsers.passwordTooltip")}
                        IconProps={{ style: { marginTop: 13, marginLeft: 8 } }}
                    />
                </Box>

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Button
                        fullWidth
                        disableElevation
                        variant="contained"
                        type="button"
                        disabled={isCreatingUser || isResettingPassword || isReadOnly}
                        onClick={onClickSendPasswordReset}
                    >
                        {isResettingPassword
                            ? t("view.admin.sendPasswordResetButtonLoading")
                            : t("view.admin.sendPasswordResetButton")}
                    </Button>

                    <HelpTooltip
                        title={t("view.adminUsers.emailTooltip")}
                        IconProps={{ style: { marginLeft: 8 } }}
                    />
                </Box>
            </FormControl>

            {isSuperadmin && (
                <FormControl fullWidth margin="normal">
                    <InputLabel {...(!formData.company && { shrink: true })}>
                        {t("view.admin.company")}
                    </InputLabel>
                    <Select
                        displayEmpty
                        value={hasCompanies && formData.company ? formData.company.id : ""}
                        onChange={onChange("company")}
                        error={Boolean(error?.company)}
                        {...(hasCompanies && {
                            renderValue: selected => {
                                if (selected === "") return t("view.adminUsers.noCompany")
                                const company = companies.find(({ id }) => selected === id)
                                return (
                                    <CompanyMenuItem showSummary={isEnabled} company={company} />
                                )
                            }
                        })}
                    >
                        <MenuItem value="">No company</MenuItem>
                        {hasCompanies &&
                            companies.map(company => {
                                const isInitialCompany = initialCompany?.id === company.id

                                // Make sure to allow admin to select user's initial company, whether
                                // that company has available licenses or not.
                                let disabled = getCompanyAvailableLicenses(company) <= 0
                                if (
                                    (!isCreatingUser && isInitialCompany) ||
                                    company.unlimitedUserLicenses
                                ) {
                                    disabled = false
                                }

                                // Disabled user does not count affect licenses.
                                if (!isEnabled) {
                                    disabled = false
                                }

                                return (
                                    <MenuItem
                                        key={company.id}
                                        value={company.id}
                                        disabled={disabled}
                                    >
                                        <CompanyMenuItem
                                            company={company}
                                            showSummary={isEnabled}
                                        />
                                    </MenuItem>
                                )
                            })}
                    </Select>
                    <FormHelperText error>{error?.company}</FormHelperText>
                </FormControl>
            )}

            <PasswordResetDialog
                open={showConfirmResetPassword}
                isLoading={isResettingPassword}
                onConfirm={onConfirmSendPasswordReset}
                onClose={onCloseConfirmResetPassword}
            />
        </form>
    )
}

const CompanyMenuItem = props => {
    const { t } = useTranslation()
    const { company, showDefault, showSummary } = props
    const { name, userLicenses, unlimitedUserLicenses } = company
    const availableLicenses = getCompanyAvailableLicenses(company)

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
        >
            <Typography component="span">
                {name}
                {showDefault && <DefaultText />}
            </Typography>
            {showSummary && (
                <Typography component="span" variant="body2" color="textSecondary">
                    {unlimitedUserLicenses
                        ? t("view.adminUsers.unlimitedLicenses")
                        : t("view.adminUsers.totalUsersOverLicenses", {
                            total: availableLicenses,
                            licenses: userLicenses
                        })}
                </Typography>
            )}
        </Box>
    )
}

const DefaultText = () => {
    return (
        <Typography component="i" variant="body2" color="textSecondary">
            &nbsp;default
        </Typography>
    )
}

function getCompanyAvailableLicenses(company) {
    if (company.unlimitedUserLicenses) {
        return Infinity
    }

    const enabledUsers = company.users.filter(user => user.enabled)
    return company.userLicenses - enabledUsers.length
}

export default Form
